// Action button cluster (copy, open, preview, delete) for a hosted site row — desktop hover-reveal or mobile full-width

import { Copy, ExternalLink, Globe, Trash2, Check } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { HostedSite } from '../../hooks/use-hosted-sites'

interface SiteActionsProps {
  site: HostedSite
  isDark: boolean
  copiedId: string | null
  previewSite: HostedSite | null
  publicUrl: string
  onCopyLink: (site: HostedSite) => void
  onTogglePreview: (site: HostedSite) => void
  onDelete: (site: HostedSite) => void
  variant: 'desktop' | 'mobile'
}

export function SiteActions({ site, isDark, copiedId, previewSite: _previewSite, publicUrl, onCopyLink, onTogglePreview, onDelete, variant }: SiteActionsProps) {
  if (variant === 'desktop') {
    return (
      <div className="hidden shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 sm:flex">
        <button
          onClick={() => onCopyLink(site)}
          className={cn('rounded-md p-1.5', isDark ? 'hover:bg-surface-3 text-neutral-400 hover:text-neutral-200' : 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700')}
          title="Copy link"
        >
          {copiedId === site.id ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        <a
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn('rounded-md p-1.5', isDark ? 'hover:bg-surface-3 text-neutral-400 hover:text-neutral-200' : 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700')}
          title="Open in new tab"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
        <button
          onClick={() => onTogglePreview(site)}
          className={cn('rounded-md p-1.5', isDark ? 'hover:bg-surface-3 text-neutral-400 hover:text-neutral-200' : 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700')}
          title="Preview"
        >
          <Globe className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onDelete(site)}
          className={cn('rounded-md p-1.5', isDark ? 'hover:bg-surface-3 text-red-400/70 hover:text-red-400' : 'hover:bg-neutral-100 text-red-400/70 hover:text-red-500')}
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  // mobile: always-visible, full-width row
  return (
    <div className={cn('mt-2 flex items-center gap-1 border-t pt-2 sm:hidden', isDark ? 'border-white/[0.06]' : 'border-neutral-100')}>
      <button
        onClick={() => onCopyLink(site)}
        className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md p-2 text-xs', isDark ? 'text-neutral-400 active:bg-surface-3' : 'text-neutral-500 active:bg-neutral-100')}
      >
        {copiedId === site.id ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
        Copy
      </button>
      <a
        href={publicUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md p-2 text-xs', isDark ? 'text-neutral-400 active:bg-surface-3' : 'text-neutral-500 active:bg-neutral-100')}
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Open
      </a>
      <button
        onClick={() => onTogglePreview(site)}
        className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md p-2 text-xs', isDark ? 'text-neutral-400 active:bg-surface-3' : 'text-neutral-500 active:bg-neutral-100')}
      >
        <Globe className="h-3.5 w-3.5" />
        Preview
      </button>
      <button
        onClick={() => onDelete(site)}
        className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md p-2 text-xs text-red-400', isDark ? 'active:bg-surface-3' : 'active:bg-neutral-100')}
      >
        <Trash2 className="h-3.5 w-3.5" />
        Delete
      </button>
    </div>
  )
}
